import { useCallback, useMemo } from "react";
import { useAtom, useSetAtom } from "jotai";
import { rawThemeAtom, themeAtom } from "@humansignal/ui";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ThemeSegment.prefix.css";

/**
 * Compact three-way theme picker (Auto / Light / Dark). Used in the
 * user dropdown where a single toggle + loose label felt out of place.
 * Shares storage + atom with the top-level ThemeToggle so whichever
 * control the user interacts with, the rest of the app sees it.
 */
const PREFERRED_COLOR_SCHEME_KEY = "preferred-color-scheme";
const OPTIONS = [
  {
    value: "Auto",
    label: "Auto",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 3v18" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 3a9 9 0 010 18" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "Light",
    label: "Light",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    value: "Dark",
    label: "Dark",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
          fill="currentColor"
          fillOpacity="0.12"
        />
      </svg>
    ),
  },
];

const block = cn("theme-segment");

export const ThemeSegment = () => {
  const { t } = useTranslation();
  // Read + write the shared raw preference atom so every mounted
  // toggle (ThemeToggle, ThemeSegment, or a future one) stays in
  // lockstep. Component-local ``useState`` was the source of the
  // "Segment shows Light but the toggle icon still shows Auto" drift.
  const [selected, setSelected] = useAtom(rawThemeAtom);
  const setThemeAtom = useSetAtom(themeAtom);

  const systemMode = useMemo(
    () =>
      typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "Dark"
        : "Light",
    [],
  );

  // Apply the user's choice: write localStorage, update the two atoms.
  // We DO NOT touch ``data-color-scheme`` here — ThemeSync is the sole
  // writer, and having multiple writers is what caused the back-button
  // flicker between the SPA and Django-served pages.
  const apply = useCallback(
    (next) => {
      window.localStorage.setItem(PREFERRED_COLOR_SCHEME_KEY, next);
      setSelected(next);
      const resolved = next === "Auto" ? systemMode : next;
      setThemeAtom(resolved);
    },
    [systemMode, setSelected, setThemeAtom],
  );

  return (
    <div className={block.toClassName()} role="group" aria-label={t("components.menubar.theme_segment.theme")}>
      {OPTIONS.map((o) => {
        const active = o.value === selected;
        return (
          <button
            key={o.value}
            type="button"
            className={block.elem("item").mod({ active }).toClassName()}
            onClick={() => apply(o.value)}
            aria-pressed={active}
            aria-label={`Theme: ${o.label}`}
          >
            {o.icon}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
};
